import React, { createContext, useContext, useState, useEffect } from 'react';

export type Language = 'ko' | 'en';

interface Translation {
  [key: string]: string | Translation;
}

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const translations: Record<Language, Translation> = {
  ko: {
    // Navigation
    dashboard: '대시보드',
    songLibrary: '곡 정보',
    recordTracker: '기록 조회',
    volforce: '볼포스',
    rivalComparisons: '라이벌 비교',
    difficultyTables: '서열표',
    recordBingo: '빙고',
    playlist: '플레이리스트',
    settings: '설정',

    // Common
    search: '검색',
    filter: '필터',
    level: '레벨',
    score: '스코어',
    exScore: 'EX 스코어',
    apply: '적용',
    cancel: '취소',
    close: '닫기',
    save: '저장',
    delete: '삭제',
    add: '추가',
    edit: '수정',
    view: '보기',
    loading: '로딩 중...',
    error: '오류',
    retry: '다시 시도',

    // Volforce
    volforceTable: '볼포스 표',
    topPatterns: '상위 {count}개 패턴으로 계산된 볼포스',
    exportImage: '이미지로 내보내기',
    calculatedFrom: '{count}개 패턴으로 계산됨',

    // Rivals
    rivals: '라이벌',
    myRivals: '내 라이벌',
    addRival: '라이벌 추가',
    addYourFirstRival: '첫 번째 라이벌 추가',
    noRivalsYet: '아직 추가된 라이벌이 없습니다',
    addRivalsToCompare: '라이벌을 추가하여 점수를 비교하고 진행 상황을 추적하세요',
    searchByUsername: '사용자명 또는 SDVX ID로 검색',
    enterUsername: '사용자명 또는 SDVX ID를 입력하세요...',
    noPlayersFound: '플레이어를 찾을 수 없습니다',
    levelRange: '레벨 범위',
    compareBy: '비교 기준',
    applyFilters: '필터 적용',
    legend: '범례',
    win: '승 (높은 점수)',
    draw: '무 (같은 점수)',
    loss: '패 (낮은 점수)',
    notCompared: '비교 안됨 (한 명 또는 둘 다 플레이하지 않음)',
    songsNotCompared: '{count}곡 비교 안됨',
    wins: '승',
    draws: '무',
    losses: '패',
    winRate: '승률',
    lastUpdated: '마지막 업데이트',
    viewDetailedComparison: '자세한 비교 보기',
    addNewRival: '새 라이벌 추가',

    // Song Library
    searchSongs: '곡 검색...',
    noSongsFound: '조건에 맞는 곡이 없습니다.',
    loadingMoreSongs: '더 많은 곡을 불러오는 중...',
    reachedEndOfList: '곡 목록의 끝에 도달했습니다',
    gridView: '그리드 보기',
    listView: '리스트 보기',
    konasuteOnly: '코나스테 곡만 보기',
    ownedPacksOnly: '보유한 곡만 보기',

    // Filters
    filters: '필터',
    difficulty: '난이도',
    excludeEacSongs: '코나스테 전용곡 제외',
    sortBy: '정렬 기준',
    ascending: '오름차순',
    descending: '내림차순',
    clearAllFilters: '모든 필터 지우기',
    songTitle: '곡 제목',
    artist: '아티스트',
    releaseDate: '출시일',
    playDate: '플레이 날짜',
    showNewRecords: '기록 갱신 데이터 확인',
    latest: '전체',
    vsMode: '비교',
    compareWithMyScore: '내 기록과 비교하기',
    searchPlaceholder: '곡명, 아티스트, 유의어, 초성으로 검색...',
    showBookmarks: '북마크만 보기',
    showingBookmarks: '북마크만 보기',
    noBookmarkedSongs: '북마크에 등록된 곡 없음',
    bookmarkHint: '북마크에 곡을 등록해 보세요',
    withCommentsOnly: '코멘트 있는 곡만',

    // SortOptions
    'Radar Notes': '레이더 - Notes',
    'Radar Peak': '레이더 - Peak',
    'Radar Tsumami': '레이더 - Tsumami',
    'Radar Tricky': '레이더 - Tricky',
    'Radar HandTrip': '레이더 - Hand Trip',
    'Radar OneHand': '레이더 - One Hand',

    // Login
    login: '로그인',
    logout: '로그아웃',

    // Record page
    min: '최소',
    max: '최대',
    grades: '등급',
    clearTypes: '클리어 타입',
    targetClass: '목표 클래스',
    recordType: '기록 타입',
    all: '전체',
    arcade: '아케이드',
    konasute: '코나스테',
    none: '없음',
    noFiltersApplied: '적용된 필터 없음',
    entriesShown: '{count}개 항목 표시됨',
    readyToSearch: '기록 검색 준비 완료',
    clickApplyFilters: '"필터 적용"을 클릭하여 데이터를 로드하세요',
    loadingRecords: '기록 로딩 중...',
    loadingRecordsInTable: '기록을 불러오는 중...',
    clickForRivalRankings: '라이벌 순위를 보려면 아무 기록이나 클릭하세요',
    noRecordsFound: '조건에 맞는 기록이 없습니다',
    tryAdjustingFilters: '필터나 검색 조건을 조정해 보세요',
    showVolforce: '볼포스 보기',
    excludeKonasuteSongs: '코나스테 곡 제외',
    includeRivalRanking: '라이벌 랭킹 포함',
    rivalRankings: '라이벌 랭킹',
    recordHistory: '플레이 이력',
    'Rival Ranking': '라이벌 랭킹 순',

    // Dashboard
    clearTypeDistribution: '클리어 타입 분포',
    averageScore: '평균 점수',
    averageExScorePercent: '평균 EX 점수 %',
    totalRecords: '총 기록 수',

    // Sort options
    'Level (High to Low)': '레벨 (높은 순)',
    'Level (Low to High)': '레벨 (낮은 순)',
    'Score (High to Low)': '점수 (높은 순)',
    'Score (Low to High)': '점수 (낮은 순)',
    'EX Score % (High to Low)': 'EX 점수 % (높은 순)',
    'EX Score % (Low to High)': 'EX 점수 % (낮은 순)',
    'Target Diff (High to Low)': '목표 차이 (높은 순)',
    'Target Diff (Low to High)': '목표 차이 (낮은 순)',
    'Last Update (Newest)': '플레이 이력 (최신 순)',
    'Last Update (Oldest)': '플레이 이력 (과거 순)',
    'Volforce (High to Low)': '볼포스 (높은 순)',
    'Volforce (Low to High)': '볼포스 (낮은 순)',

    // Rival Comparisons
    loadingRivals: '라이벌 정보를 불러오는 중...',
    tryAgain: '다시 시도',
    rivalUserId: '라이벌 사용자 ID',
    enterRivalUserId: '라이벌의 사용자 ID를 입력하세요...',
    enterExactUserId: '추가하려는 라이벌의 정확한 사용자 ID를 입력하세요',
    adding: '추가 중...',

    // Errors
    failedToLoad: '{item}를 불러올 수 없습니다',
  },
  en: {
    // Navigation
    dashboard: 'Dashboard',
    songLibrary: 'Song Library',
    recordTracker: 'Record Tracker',
    volforce: 'Volforce',
    rivalComparisons: 'Rival Comparisons',
    difficultyTables: 'Difficulty Tables',
    recordBingo: 'Record Bingo',
    playlist: 'Playlist',
    settings: 'Settings',

    // Common
    search: 'Search',
    filter: 'Filter',
    level: 'Level',
    score: 'Score',
    exScore: 'EX Score',
    apply: 'Apply',
    cancel: 'Cancel',
    close: 'Close',
    save: 'Save',
    delete: 'Delete',
    add: 'Add',
    edit: 'Edit',
    view: 'View',
    loading: 'Loading...',
    error: 'Error',
    retry: 'Retry',

    // Volforce
    volforceTable: 'Volforce Table',
    topPatterns: 'Volforce calculated from top {count} patterns',
    exportImage: 'Export as Image',
    calculatedFrom: 'Calculated from {count} patterns',

    // Rivals
    rivals: 'Rivals',
    myRivals: 'My Rivals',
    addRival: 'Add Rival',
    addYourFirstRival: 'Add Your First Rival',
    noRivalsYet: 'No rivals added yet',
    addRivalsToCompare: 'Add rivals to compare your scores and track your progress',
    searchByUsername: 'Search by Username or SDVX ID',
    enterUsername: 'Enter username or SDVX ID...',
    noPlayersFound: 'No players found',
    levelRange: 'Level Range',
    compareBy: 'Compare by',
    applyFilters: 'Apply Filters',
    legend: 'Legend',
    win: 'Win (Higher score)',
    draw: 'Draw (Same score)',
    loss: 'Loss (Lower score)',
    notCompared: 'Not Compared (One or both haven\'t played)',
    songsNotCompared: '{count} songs not compared',
    wins: 'Wins',
    draws: 'Draws',
    losses: 'Losses',
    winRate: 'Win Rate',
    lastUpdated: 'Last updated',
    viewDetailedComparison: 'View Detailed Comparison',
    addNewRival: 'Add New Rival',

    // Song Library
    searchSongs: 'Search songs...',
    noSongsFound: 'No songs found matching your criteria.',
    loadingMoreSongs: 'Loading more songs...',
    reachedEndOfList: 'You\'ve reached the end of the song list',
    gridView: 'Grid View',
    listView: 'List View',

    'Radar Notes': 'Radar - Notes',

    // Filters
    filters: 'Filters',
    difficulty: 'Difficulty',
    excludeEacSongs: 'Exclude EAC Songs',
    sortBy: 'Sort By',
    ascending: 'Ascending',
    descending: 'Descending',
    clearAllFilters: 'Clear All Filters',
    songTitle: 'Song Title',
    artist: 'Artist',
    releaseDate: 'Release Date',

    // Record page
    min: 'Min',
    max: 'Max',
    grades: 'Grades',
    clearTypes: 'Clear Types',
    targetClass: 'Target Class',
    recordType: 'Record Type',
    all: 'All',
    arcade: 'Arcade',
    konasute: 'Konasute',
    none: 'None',
    noFiltersApplied: 'No filters applied',
    entriesShown: '{count} entries shown',
    readyToSearch: 'Ready to search your records',
    clickApplyFilters: 'Click "Apply Filters" to load your data',
    loadingRecords: 'Loading records...',
    loadingRecordsInTable: 'Loading records...',
    clickForRivalRankings: 'Click any record to view rival rankings',
    noRecordsFound: 'No records found matching your criteria.',
    tryAdjustingFilters: 'Try adjusting your filters or search terms.',

    // Dashboard
    clearTypeDistribution: 'Clear Type Distribution',
    averageScore: 'Average Score',
    averageExScorePercent: 'Average EX Score %',
    totalRecords: 'Total Records',

    // Sort options
    'Level (High to Low)': 'Level (High to Low)',
    'Level (Low to High)': 'Level (Low to High)',
    'Score (High to Low)': 'Score (High to Low)',
    'Score (Low to High)': 'Score (Low to High)',
    'EX Score % (High to Low)': 'EX Score % (High to Low)',
    'EX Score % (Low to High)': 'EX Score % (Low to High)',
    'Target Diff (High to Low)': 'Target Diff (High to Low)',
    'Target Diff (Low to High)': 'Target Diff (Low to High)',

    // Login
    login: 'Login',
    logout: 'Logout',

    // Rival Comparisons
    loadingRivals: 'Loading rival information...',
    tryAgain: 'Try Again',
    rivalUserId: 'Rival User ID',
    enterRivalUserId: 'Enter rival\'s user ID...',
    enterExactUserId: 'Enter the exact user ID of the rival you want to add',
    adding: 'Adding...',

    // Errors
    failedToLoad: 'Failed to load {item}',
  }
};

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguage] = useState<Language>('ko');

  useEffect(() => {
    // Load saved language from localStorage
    const savedLanguage = localStorage.getItem('language') as Language;
    if (savedLanguage && ['ko', 'en'].includes(savedLanguage)) {
      setLanguage(savedLanguage);
    }
  }, []);

  const handleSetLanguage = (lang: Language) => {
    setLanguage(lang);
    localStorage.setItem('language', lang);
  };

  const t = (key: string): string => {
    const keys = key.split('.');
    let value: string | Translation = translations[language];

    for (const k of keys) {
      if (typeof value === 'object' && value[k] !== undefined) {
        value = value[k];
      } else {
        // Fallback to Korean if key not found in current language
        value = translations.ko;
        for (const fallbackKey of keys) {
          if (typeof value === 'object' && value[fallbackKey] !== undefined) {
            value = value[fallbackKey];
          } else {
            return key; // Return key if not found in fallback either
          }
        }
        break;
      }
    }

    return typeof value === 'string' ? value : key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage: handleSetLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageContext;
